import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiStar,
  FiTruck,
  FiUser,
  FiMail,
  FiPhone,
} from 'react-icons/fi';
import { Card, LoaderComponent } from '../../components/ui';
import driverService, { DriverProfile as DriverProfileType } from '../../services/driver.service';

const vehicleLabels: Record<string, string> = {
  MOTO: 'Moto',
  CARRO: 'Carro',
  BICICLETA: 'Bicicleta',
  A_PE: 'A pe',
};

const vehicleOptions = ['MOTO', 'CARRO', 'BICICLETA', 'A_PE'] as const;

const DriverProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<DriverProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vehicleType, setVehicleType] = useState('MOTO');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await driverService.getProfile();
        setProfile(res.data.data);
        setVehicleType(res.data.data.vehicleType || 'MOTO');
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await driverService.updateProfile({ vehicleType });
      alert('Perfil atualizado!');
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoaderComponent />;
  if (!profile) return null;

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/driver')} className="p-2 hover:bg-gray-100 rounded-lg">
          <FiArrowLeft />
        </button>
        <h1 className="text-xl font-bold">Meu perfil</h1>
      </div>

      {/* Avatar e info */}
      <Card className="p-6 text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-pagali-primary/10 flex items-center justify-center mb-3">
          {profile.avatar ? (
            <img src={profile.avatar} alt="" className="w-20 h-20 rounded-full object-cover" />
          ) : (
            <FiUser className="text-3xl text-pagali-primary" />
          )}
        </div>
        <h2 className="text-xl font-bold">{profile.name}</h2>
        <div className="flex items-center justify-center gap-4 mt-2 text-sm text-gray-500">
          <span className="flex items-center gap-1"><FiMail /> {profile.email}</span>
          {profile.phone && <span className="flex items-center gap-1"><FiPhone /> {profile.phone}</span>}
        </div>
        <div className="flex items-center justify-center gap-6 mt-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-pagali-primary">{profile.totalEntregas}</p>
            <p className="text-xs text-gray-500">Entregas</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-500 flex items-center justify-center gap-1">
              <FiStar /> {Number(profile.ratingMedio || 0).toFixed(1)}
            </p>
            <p className="text-xs text-gray-500">Avaliacao</p>
          </div>
        </div>
      </Card>

      {/* Tipo de veiculo */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <FiTruck /> Tipo de veiculo
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {vehicleOptions.map((v) => (
            <button
              key={v}
              onClick={() => setVehicleType(v)}
              className={`px-4 py-3 rounded-xl border-2 text-sm font-medium transition-colors ${
                vehicleType === v
                  ? 'border-pagali-primary bg-pagali-primary/5 text-pagali-primary'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {vehicleLabels[v]}
            </button>
          ))}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="mt-4 w-full py-3 bg-pagali-primary text-white rounded-xl font-medium hover:bg-pagali-primary/90 disabled:opacity-50"
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </Card>
    </div>
  );
};

export default DriverProfilePage;
